import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const AuthContext = createContext(null);

axios.defaults.withCredentials = true;

function formatApiError(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function guardarToken(token) {
  if (token) localStorage.setItem("auth_token", token);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Com 2FA ativo, a senha certa devolve so um "desafio": a sessao so e criada
  // depois do codigo do app autenticador (login2fa).
  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.requer_2fa) return res.data;
    guardarToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const login2fa = async (desafio, codigo) => {
    const res = await axios.post(`${API}/auth/login/2fa`, { desafio, codigo });
    guardarToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    localStorage.removeItem("auth_token");
    setUser(false);
  };

  const setUserDirect = (userData) => {
    if (userData?.token) guardarToken(userData.token);
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, login2fa, logout, setUserDirect, refreshUser: checkAuth, guardarToken, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
